import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
} from "@tanstack/react-query";
import { adminRequest, type AdminRequestOptions } from "@/services/api/admin-request";
import type { AdminEndpointKey, AdminApiError } from "@/services/api/admin-registry";

// ---------------------------------------------------------------------------
// useAdminQuery — generic GET wrapper
// ---------------------------------------------------------------------------

interface UseAdminQueryOpts<T> {
  endpointKey: AdminEndpointKey;
  requestOpts?: AdminRequestOptions;
  queryOpts?: Omit<UseQueryOptions<T, AdminApiError>, "queryKey" | "queryFn">;
}

export function useAdminQuery<T = unknown>({
  endpointKey,
  requestOpts,
  queryOpts,
}: UseAdminQueryOpts<T>) {
  return useQuery<T, AdminApiError>({
    queryKey: ["admin", endpointKey, requestOpts?.params, requestOpts?.query],
    queryFn: async () => {
      const result = await adminRequest<T>(endpointKey, requestOpts);
      return result.data;
    },
    retry: (failureCount, err) => {
      if (
        ["auth", "forbidden", "not_found", "validation", "superadmin_policy"].includes(err.kind)
      ) {
        return false;
      }
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    ...queryOpts,
  });
}

// ---------------------------------------------------------------------------
// useAdminMutation — generic POST/PUT/PATCH/DELETE wrapper
// ---------------------------------------------------------------------------

interface UseAdminMutationOpts<TData, TVariables> {
  endpointKey: AdminEndpointKey;
  invalidateKeys?: string[][];
  requestOpts?: Omit<AdminRequestOptions, "body">;
  /** Extract params/query/body from variables. When omitted, variables is used as body. */
  mapVariables?: (variables: TVariables) => Omit<AdminRequestOptions, "isSuperadmin">;
  mutationOpts?: Omit<
    UseMutationOptions<{ data: TData; correlationId?: string }, AdminApiError, TVariables>,
    "mutationFn"
  >;
}

export function useAdminMutation<TData = unknown, TVariables = unknown>({
  endpointKey,
  invalidateKeys,
  requestOpts,
  mapVariables,
  mutationOpts,
}: UseAdminMutationOpts<TData, TVariables>) {
  const queryClient = useQueryClient();

  return useMutation<{ data: TData; correlationId?: string }, AdminApiError, TVariables>({
    ...mutationOpts,
    mutationFn: async (variables) => {
      const mapped = mapVariables
        ? mapVariables(variables)
        : { body: variables };
      const result = await adminRequest<TData>(endpointKey, {
        ...requestOpts,
        ...mapped,
      });
      return { data: result.data, correlationId: result.correlationId };
    },
    retry: false,
    onSuccess: (...args) => {
      if (invalidateKeys) {
        for (const key of invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
      mutationOpts?.onSuccess?.(...args);
    },
  });
}
